<script setup lang="ts">
import { nextTick, ref, watch } from "vue";

interface TextareaProps {
	schema?: "primary" | "white";
	modelValue?: string;
	id?: string;
	label?: string;
	placeholder?: string;
	disabled?: boolean;
	required?: boolean;
	autofocus?: boolean;
	lazy?: boolean;
	iconClassLeft?: string;
	iconHtmlLeft?: string;
	iconClassRight?: string;
	small?: boolean;
	success?: boolean;
	error?: boolean;
	rows?: number;
	maxRows?: number;
	minHeight?: number;
}

const props = withDefaults(defineProps<TextareaProps>(), {
	schema: "primary",
	modelValue: undefined,
	id: undefined,
	label: undefined,
	placeholder: undefined,
	disabled: false,
	required: false,
	autofocus: false,
	lazy: false,
	iconClassLeft: undefined,
	iconHtmlLeft: undefined,
	iconClassRight: undefined,
	small: false,
	success: false,
	error: false,
	rows: 2,
	maxRows: undefined,
	minHeight: undefined,
});

const textareaEl = ref<HTMLTextAreaElement>();
const isFocus = ref(false);

const emit = defineEmits(["update:modelValue"]);

function onInput(e: Event) {
	const target = e.target as HTMLTextAreaElement;
	emit("update:modelValue", target.value);

	// Auto resize
	if (textareaEl.value) {
		textareaEl.value.style.height = "auto";

		let newHeight = textareaEl.value.scrollHeight;

		// Apply minHeight if provided
		if (props.minHeight && newHeight < props.minHeight) {
			newHeight = props.minHeight;
		}

		// Apply maxRows limit if provided
		if (props.maxRows) {
			const lineHeight =
				Number.parseInt(getComputedStyle(textareaEl.value).lineHeight) || 20;
			const maxHeight = lineHeight * props.maxRows;
			if (newHeight > maxHeight) {
				newHeight = maxHeight;
				textareaEl.value.style.overflowY = "auto";
			} else {
				textareaEl.value.style.overflowY = "hidden";
			}
		}

		textareaEl.value.style.height = `${newHeight}px`;
	}
}

// Resize on initial load and when modelValue changes
watch(
	() => props.modelValue,
	() => {
		nextTick(() => {
			if (textareaEl.value) {
				onInput({ target: textareaEl.value } as unknown as Event);
			}
		});
	},
	{ immediate: true },
);
</script>

<template>
	<div
		class="new-input"
		:class="[
			{
				'new-input_disabled': disabled,
				'new-input_small': small,
				success: success,
				error: error,
			},
			schema,
		]"
	>
		<label
			v-if="label"
			class="new-input__label"
			:for="id"
		>
			{{ label }}
		</label>
		<div class="new-input__wrap textarea-wrap" @click="textareaEl?.focus()">
			<div
				v-if="iconHtmlLeft"
				v-html="iconHtmlLeft"
			></div>
			<span
				v-if="iconClassLeft"
				class="new-input__icon-left"
			>
				<i
					class="icon"
					:class="iconClassLeft"
				></i>
			</span>
			<slot name="iconLeft"></slot>
			<textarea
				v-bind="$attrs"
				:id="id"
				ref="textareaEl"
				:value="modelValue"
				class="new-input__input textarea"
				:placeholder="placeholder"
				:disabled="disabled"
				:required="required"
				:rows="rows"
				:autofocus="autofocus"
				@input="onInput"
				@focus="isFocus = true"
				@blur="isFocus = false"
			></textarea>
			<slot name="iconRight"></slot>
			<span
				v-if="iconClassRight"
				class="new-input__icon-right"
			>
				<i
					class="icon"
					:class="iconClassRight"
				></i>
			</span>
		</div>
		<slot></slot>
	</div>
</template>

<style lang="scss">
.new-input {
	.textarea-wrap {
		height: auto;
		min-height: 60px;
		align-items: flex-start;
		
		@include media-breakpoint-down(sm) {
			min-height: 52px;
		}
	}
	
	&.new-input_small {
		.textarea-wrap {
			min-height: 40px;
		}
	}
	
	.textarea {
		resize: none;
		overflow-y: hidden;
		height: auto;
		padding: 4px 0;
		line-height: 24px;
		
		@include media-breakpoint-down(sm) {
			line-height: 22px;
		}
	}
}
</style>
